import { InjectionKey } from 'vue'
import organisation from './organisation'
import permission from './permission';
import sidebar from './sidebar';
import courseActivity from './courseActivity';
import lmsCourse from './lmsCourse'
import superAdmin from './superAdmin';
import courseAssignment from './courseAssignment';
import reminderLog from './reminderLog';
import instruction from './instruction';
import instructionContent from './instructionContent';
import instructionLibrary from './instructionLibrary';
import { createStore, useStore as baseUseStore, Store } from 'vuex'
import { Auth } from 'aws-amplify';

import { TState } from '@/types/TState';
import { show as showProfile, store as storeProfile } from '@training24/user/src/profile'

const API_URL = '/api'

// define injection key
export const key: InjectionKey<Store<TState>> = Symbol()

export const store = createStore<TState>({
  state: {
    userSession: null,
    user: null,
    userDetailsLoaded: false,
    pendingCognitoUser: null,
  },
  getters: {
    getUser(state) {
      return state.user
    },
    getUserDetailsLoaded(state) {
      return state.userDetailsLoaded
    },
    getUserSession(state) {
      return state.userSession
    },
    getEMAIL(state) {
      return state.userSession.attributes.email;
    },
    getUserId(state) {
      return state.userSession.attributes.sub;
    },
    getUsername(state) {
      return state.userSession.attributes.usernam;
    },
    getJWT(state) {
      return state.userSession ? state.userSession.signInUserSession?.idToken.jwtToken : null;
    }
  },
  mutations: {
    setUserDetailsLoaded(state) {
      state.userDetailsLoaded = true
    },
    setUser(state, user) {
      state.user = user
    },
    setUserSession(state, session) {
      state.userSession = session
    },
    setUserSignInToken(state, session) {
      state.userSession = { ...state.userSession, signInUserSession: session }
    },
    setPendingCognitoUser(state, user) {
      state.pendingCognitoUser = user;
    }
  },
  actions: {
    async getUserDate(context) {
      context.dispatch('organisation/LOAD');
      
      if (context.getters.getUser) {
        return context.getters.getUser;
      }
      const result = await showProfile(context.getters.getJWT);
      context.commit('setUserDetailsLoaded');
      if (result.data.first_name && result.data.last_name) {
        // consentGiven liegt auf Profil-Ebene (nicht in data) – fürs Erstlogin-Formular mitführen
        context.commit('setUser', { ...result.data, consentGiven: (result as any).consentGiven });
        return result.data;
      }
      return;
    },
    // OTP Step 1: initiate custom auth, triggers CreateAuthChallenge Lambda
    async requestOtp(context, email: string) {
      context.commit('setPendingCognitoUser', null);
      const cognitoUser = await Auth.signIn(email);
      context.commit('setPendingCognitoUser', cognitoUser);
    },

    // OTP Step 2: verify code — Hub fires 'signIn' on success, router handles redirect
    async verifyOtp(context, code: string) {
      const cognitoUser = context.state.pendingCognitoUser;
      if (!cognitoUser) throw new Error('Keine aktive OTP-Sitzung.');
      const result = await Auth.sendCustomChallengeAnswer(cognitoUser, code);
      if (result.signInUserSession) {
        context.commit('setPendingCognitoUser', null);
      }
      return result;
    },

    async saveUser(context, payload) {
      const result = await storeProfile({
        first_name: payload.first_name,
        last_name: payload.last_name,
        consentGiven: payload.consentGiven,
      } as any, context.getters.getJWT);
      context.commit('setUser', { ...result.data, consentGiven: (result as any).consentGiven })
      return payload
    }
  },
  modules: {
    organisation,
    permission,
    sidebar,
    courseActivity,
    lmsCourse,
    superAdmin,
    courseAssignment,
    reminderLog,
    instruction,
    instructionContent,
    instructionLibrary,
  }
})

export function useStore() {
  return baseUseStore(key)
}